import { FC, ReactNode, useEffect, useState } from 'react'
import {
  useFloating,
  shift,
  offset,
  flip,
  useHover,
  useClick,
  useInteractions,
  safePolygon,
  Placement,
  useDismiss,
  useFocus,
} from '@floating-ui/react'
import { useFloatingDismiss } from 'src/hooks'

type PopoutProps = {
  contents: ReactNode
  onIsOpen?: (arg: boolean) => void
  children: (args: { isOpen: boolean }) => ReactNode
  placement?: Placement
  dismissFloatingEnabled?: boolean
  hoverEnabled?: boolean
  clickEnabled?: boolean
  ignoreMouse?: boolean
  floatingOffset?: // this type is not exported from the library but comes from the docs
  | number
    | {
        mainAxis?: number
        crossAxis?: number
        alignmentAxis?: number | null
      }
  popoutClass?: string
  focusEnabled?: boolean
  focusKeyboardOnly?: boolean
  wrapperStyles?: string
}

/* This Tooltip component wraps around your component which you want to have a popout effect. It will show the popout anytime your wrapped component is hovered. It passes back `isOpen` so you can apply styles to the child if needed */
const Popout: FC<PopoutProps> = ({
  children,
  onIsOpen,
  contents,
  placement = 'top',
  hoverEnabled = true,
  clickEnabled = false,
  dismissFloatingEnabled = false,
  ignoreMouse = true, // for keyboard only click events when hovering,
  floatingOffset = 3,
  popoutClass,
  focusEnabled,
  focusKeyboardOnly,
  wrapperStyles,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { x, y, strategy, refs, context } = useFloating({
    placement,
    middleware: [
      offset(floatingOffset), // pushes the float out
      flip(), // will flip if scrolling
      shift({
        padding: 5, // padding from the window edge
      }),
    ],
    open: isOpen,
    onOpenChange: setIsOpen,
  })
  const hover = useHover(context, {
    enabled: hoverEnabled,
    handleClose: safePolygon({
      blockPointerEvents: true,
      buffer: 1, // allows the Popout to remain open with a mouse buffer
    }),
    restMs: 10,
  })
  const click = useClick(context, {
    enabled: clickEnabled,
    ignoreMouse,
  })
  const dismiss = useDismiss(context)
  const custom = useFloatingDismiss(context, {
    enabled: dismissFloatingEnabled,
  })
  const focus = useFocus(context, {
    enabled: focusEnabled,
    keyboardOnly: focusKeyboardOnly,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
    custom,
    focus,
  ])

  useEffect(() => {
    if (onIsOpen) onIsOpen(isOpen)
  }, [isOpen, onIsOpen])

  return (
    <>
      <div
        className={wrapperStyles}
        {...getReferenceProps()}
        ref={refs.setReference}
      >
        {children({ isOpen })}
      </div>
      {isOpen && (
        <div
          className={`z-40 ${popoutClass}`}
          ref={refs.setFloating}
          {...getFloatingProps()}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            width: 'max-content',
          }}
        >
          {contents}
        </div>
      )}
    </>
  )
}

export default Popout
